import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { sayAlex, stopAlex } from '@/audio/alex';
import { labBunny } from '@/content/helpers';
import { ELEVATOR_BEAT } from '@/content/reports';
import { stripTags } from '@/content/story';
import { clearElevatorBeat, peekElevatorBeat } from '@/game/beats';
import { assets, imageByPath, type ImageAsset } from '@/assets';
import { navigate } from '@/app/router';
import { floorIds, floors } from '@/content/floors';
import { commandCenter } from '@/content/scenes';
import { strings } from '@/content/strings';
import { SettingsDialog } from '@/features/settings/SettingsDialog';
import { floorProgress, isUnlocked } from '@/game/progress';
import { useProgress } from '@/game/progressStore';
import { useSettings } from '@/game/settings';
import { cssVars } from '@/lib/cssVars';
import { cx } from '@/lib/cx';
import { useOrientation, type Orientation } from '@/lib/orientation';
import { CoverSpace } from '@/pixi/CoverSpace';
import { DustMotes } from '@/pixi/effects/DustMotes';
import { GlowLights } from '@/pixi/effects/GlowLights';
import { GameStage } from '@/ui/GameStage';
import { ScreenEmblem } from '@/ui/ScreenEmblem';
import { TopBar } from '@/ui/TopBar';
import { useAgent } from '@/game/agentProfile';
import { useLab } from '@/game/labProgress';
import { elevatorMenu } from '@/content/menus';
import { useVoiceMenu } from '@/speech/control/useVoiceMenu';
import { AgentFigure } from '@/ui/AgentFigure';
import { MicButton } from '@/ui/MicButton';
import { FloorCard } from './FloorCard';
import { FloorDossier } from './FloorDossier';
import styles from './FoeElevator.module.css';

/** How long the cab rides to a floor before its mission opens. */
const RIDE_MS = 900;

const backgrounds = {
  portrait: imageByPath['backgrounds/elevator-portrait'] ?? commandCenter.portrait.background,
  landscape: imageByPath['backgrounds/elevator-landscape'] ?? commandCenter.landscape.background,
};
const cab = imageByPath['objects/elevator-cab'];

/** Where a hall painting's glass tube is, as fractions of the image width. */
interface PaintedShaft {
  image: ImageAsset;
  centre: number;
  width: number;
}

/** The hall paintings have the glass shaft built in; the cab rides that one. */
const PAINTED: Record<Orientation, PaintedShaft | undefined> = {
  portrait: painted('backgrounds/elevator-portrait', 128 / 941, 150 / 941),
  landscape: painted('backgrounds/elevator-landscape', 190 / 1672, 190 / 1672),
};

function painted(path: string, centre: number, width: number): PaintedShaft | undefined {
  const image = imageByPath[path];
  return image ? { image, centre, width } : undefined;
}

/**
 * Keeps the tower's shaft on the painted tube: the stage paints the background with `cover`,
 * anchored to the left edge, so the tube's place on screen follows from the viewport size.
 */
function usePaintedShaft(tower: RefObject<HTMLElement | null>, shaft: PaintedShaft | undefined) {
  useLayoutEffect(() => {
    const el = tower.current;
    if (!el || !shaft) return;
    const place = () => {
      const { innerWidth: w, innerHeight: h } = window;
      const scale = Math.max(w / shaft.image.width, h / shaft.image.height);
      const left = el.getBoundingClientRect().left;
      el.style.setProperty(
        '--shaft-x',
        `${String(shaft.centre * shaft.image.width * scale - left)}px`,
      );
      el.style.setProperty('--shaft-w', `${String(shaft.width * shaft.image.width * scale)}px`);
    };
    place();
    const observer = new ResizeObserver(place);
    observer.observe(document.documentElement);
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, [tower, shaft]);
}

/**
 * The Foe Elevator: the hub between missions. Each floor is a foe and an /r/ context; the cab with
 * the player's agent rides to a floor to start its mission. See docs/design/foe-elevator.md.
 */
export function FoeElevator() {
  const progress = useProgress();
  // Back from a mission that unmasked a foe: Agent Alex moves the story on.
  const [beat] = useState(() => {
    const kind = peekElevatorBeat();
    const bank = kind ? ELEVATOR_BEAT[kind] : undefined;
    return bank?.[Math.floor(Math.random() * bank.length)];
  });
  const [beatShown, setBeatShown] = useState(Boolean(beat));
  useEffect(() => {
    if (!beat) return;
    let done = false;
    void sayAlex(beat).then(() => {
      clearElevatorBeat();
      if (!done)
        setTimeout(() => {
          setBeatShown(false);
        }, 1500);
    });
    return () => {
      done = true;
      stopAlex();
    };
  }, [beat]);
  // A grown-up sets the player's age before the first mission; settings can be reopened any time.
  const { age } = useSettings();
  const [settingsOpen, setSettingsOpen] = useState(age === null);
  const orientation = useOrientation();
  const view = commandCenter[orientation];
  const unlocked = floors.map((_, i) => isUnlocked(floorIds, i, progress));
  const home = Math.max(0, unlocked.lastIndexOf(true));
  const [cabAt, setCabAt] = useState(home);
  const [riding, setRiding] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const tower = useRef<HTMLDivElement>(null);
  const shaft = PAINTED[orientation];
  usePaintedShaft(tower, shaft);
  useEffect(
    () => () => {
      clearTimeout(timer.current);
    },
    [],
  );

  const agent = useAgent();
  const lab = useLab();
  // The lab opens with the cat unmasked (floor 2 open), or once its story was told.
  const labOpen = lab.reveal !== 'untold' || unlocked[1] === true;

  const landscape = orientation === 'landscape';
  const start = (index: number) => {
    const floor = floors[index];
    if (!floor || riding) return;
    setRiding(true);
    timer.current = setTimeout(
      () => {
        navigate({ page: 'floor', floorId: floor.id });
      },
      index === cabAt ? 350 : RIDE_MS,
    );
    setCabAt(index);
  };
  // Portrait: one tap rides and starts. Landscape: a tap rides there and opens the floor's
  // dossier, which has the start button.
  const choose = (index: number) => {
    if (!unlocked[index] || riding) return;
    if (landscape) setCabAt(index);
    else start(index);
  };
  const selected = floors[cabAt] ?? floors[0];

  const t = strings.elevator;
  // Voice: a floor by number, foe or sound, Alex's Lab, or the child's agent (voice-control.md).
  const openKey = unlocked.join(',');
  // A new menu drops a pending check-back, so it changes only when the open floors do.
  // The React compiler keeps this the same object while the open floors stay the same.
  const menu = elevatorMenu(
    openKey.split(',').map((v) => v === 'true'),
    labOpen,
  );
  const voice = useVoiceMenu(
    menu,
    (id) => {
      if (id === '@lab') navigate({ page: 'lab' });
      else if (id === '@agent') navigate({ page: 'agent' });
      else {
        const index = floors.findIndex((f) => f.id === id);
        if (index >= 0) start(index);
      }
    },
    (line) => {
      void sayAlex(line);
    },
  );
  const micOn = voice.state.kind !== 'off' && voice.state.kind !== 'error';

  return (
    <GameStage
      backgrounds={backgrounds}
      backgroundPosition="left center"
      scene={
        <>
          <CoverSpace
            key={orientation}
            width={view.background.width}
            height={view.background.height}
          >
            <GlowLights lights={view.lights} />
          </CoverSpace>
          <DustMotes />
        </>
      }
    >
      <div className={styles.screen}>
        <TopBar
          onSettings={() => {
            setSettingsOpen(true);
          }}
        />
        <div className={styles.emblem}>
          <ScreenEmblem
            first={t.titleTop}
            second={t.titleBottom}
            width={orientation === 'landscape' ? 300 : 280}
          />
        </div>
        <p className={styles.tagline}>{t.tagline}</p>
        <div className={styles.extras}>
          <button
            type="button"
            className={styles.extra}
            onClick={() => {
              navigate({ page: 'agent' });
            }}
          >
            {agent.made && (
              <AgentFigure look={agent} height={44} className={styles.extraArt} label="" />
            )}
            My agent
          </button>
          {labOpen && (
            <button
              type="button"
              className={styles.extra}
              onClick={() => {
                navigate({ page: 'lab' });
              }}
            >
              <img src={labBunny.portraits.clipboard.url} alt="" className={styles.extraArt} />
              Alex’s Lab
            </button>
          )}
          <MicButton
            state={
              voice.state.kind === 'listening'
                ? 'listening'
                : voice.state.kind === 'loading' || voice.state.kind === 'thinking'
                  ? 'busy'
                  : 'idle'
            }
            label={micOn ? 'Stop listening' : 'Choose by voice'}
            size={52}
            pressed={micOn}
            onPress={() => {
              if (micOn) void voice.stop();
              else void voice.start();
            }}
          />
        </div>
        {beat && beatShown && (
          <p className={styles.beat} aria-live="polite">
            <img src={labBunny.portraits.clipboard.url} alt="" draggable={false} />
            <span>{stripTags(beat)}</span>
          </p>
        )}

        <div className={styles.body}>
          <div
            ref={tower}
            className={cx(styles.tower, shaft && styles.painted)}
            style={cssVars({ '--floors': floors.length })}
          >
            <div className={styles.shaft} aria-hidden>
              <span className={styles.column} />
              <span className={styles.pad} />
              <span className={styles.rail} />
              <span
                className={styles.track}
                style={cssVars({ '--lit': floors.length - 1 - home })}
              />
              {floors.map((floor, i) => (
                <span
                  key={floor.id}
                  className={cx(styles.stop, unlocked[i] && styles.open)}
                  style={cssVars({ '--row': floors.length - 1 - i })}
                />
              ))}
              <div
                className={cx(styles.cab, riding && styles.riding)}
                style={cssVars({ '--row': floors.length - 1 - cabAt })}
              >
                {cab ? (
                  <img src={cab.url} alt="" className={styles.cabFrame} draggable={false} />
                ) : (
                  <span className={styles.cabFallback} />
                )}
                {agent.made ? (
                  <AgentFigure look={agent} height={96} className={styles.agent} label="" />
                ) : (
                  <img
                    src={assets.characters.agent.standing.url}
                    alt=""
                    className={styles.agent}
                    draggable={false}
                  />
                )}
              </div>
            </div>

            <ol className={styles.floors}>
              {[...floors].reverse().map((floor) => {
                const index = floor.number - 1;
                return (
                  <li key={floor.id} className={styles.floor}>
                    <FloorCard
                      floor={floor}
                      progress={floorProgress(progress, floor.id)}
                      locked={!unlocked[index]}
                      current={index === cabAt}
                      unlockFrom={floor.number - 1}
                      onSelect={() => {
                        choose(index);
                      }}
                    />
                  </li>
                );
              })}
            </ol>
          </div>
          {landscape && selected && (
            <FloorDossier
              floor={selected}
              progress={floorProgress(progress, selected.id)}
              onStart={() => {
                start(cabAt);
              }}
            />
          )}
        </div>
      </div>
      {settingsOpen && (
        <SettingsDialog
          onClose={() => {
            setSettingsOpen(false);
          }}
        />
      )}
    </GameStage>
  );
}
